import { useCallback, useEffect, useMemo, useState } from "react";

import {
  crearUsuario,
  darContrasena,
  editarUsuario,
  listarCapacidades,
  listarPerfiles,
  listarTerritorio,
  listarUsuarios,
  type Capacidad,
  type NodoTerritorio,
  type Usuario,
  type Yo,
} from "./api";
import { asignables as soloAsignables, caminos } from "./arbol";
import { Cargando, ErrorDeCarga, Insignia, Pantalla, Tabla, type Columna } from "./componentes";
import { fecha } from "./formato";
import { mensajeDeError } from "./mensajes";
import { aplicarPerfil, etiquetaDePerfil, perfilDe, type Perfil } from "./perfiles";
import { sinAcentos } from "./texto";

interface Borrador {
  usuario: string;
  nombre: string;
  territorio_id: number | null;
  capacidades: Record<string, boolean>;
}

const VACIO: Borrador = { usuario: "", nombre: "", territorio_id: null, capacidades: {} };

type Filtro = "todas" | "activas" | "inactivas" | "sin-contrasena";

/**
 * CUENTAS, en el idioma de quien reparte trabajo.
 *
 * Dar de alta es elegir un OFICIO —«Capturista de campo», «Coordinación
 * territorial»— y un territorio. Las treinta capacidades del catálogo
 * existen y se pueden tocar, pero detrás de un «ajuste fino» que casi nadie
 * abre. El perfil no se guarda: se deduce de las capacidades (db/044,
 * `perfiles.ts`), así que una cuenta con una casilla de más dice «a la
 * medida» y no miente con el nombre de un oficio que ya no es.
 */
export function Usuarios({
  yo,
  parametros,
  avisar,
}: {
  yo: Yo;
  parametros: Record<string, unknown>;
  avisar: (t: string) => void;
}) {
  const [filas, setFilas] = useState<Usuario[]>([]);
  const [capacidades, setCapacidades] = useState<Capacidad[]>([]);
  const [perfiles, setPerfiles] = useState<Perfil[]>([]);
  const [nodos, setNodos] = useState<NodoTerritorio[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<Filtro>(
    parametros["filtro"] === "sin-contrasena" || parametros["filtro"] === "inactivas"
      ? (parametros["filtro"] as Filtro)
      : "todas",
  );
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [ajusteFino, setAjusteFino] = useState(false);
  // Se muestra UNA vez, igual que en el CLI: al cerrarla no hay de dónde
  // recuperarla y hay que volver a generarla.
  const [recienDada, setRecienDada] = useState<{ usuario: string; clave: string } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [u, c, p, t] = await Promise.all([
        listarUsuarios(),
        listarCapacidades(),
        listarPerfiles(),
        listarTerritorio(),
      ]);
      setFilas(u.filas);
      setCapacidades(c.capacidades);
      setPerfiles(p.perfiles);
      setNodos(t.nodos);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    if (parametros["filtro"] === "sin-contrasena" || parametros["filtro"] === "inactivas") {
      setFiltro(parametros["filtro"] as Filtro);
    }
  }, [parametros]);

  const nombreDelNodo = useMemo(() => caminos(nodos), [nodos]);
  const asignables = useMemo(() => soloAsignables(nodos), [nodos]);

  const visibles = useMemo(() => {
    const q = sinAcentos(busqueda.trim());
    return filas.filter((u) => {
      if (filtro === "activas" && !u.activo) return false;
      if (filtro === "inactivas" && u.activo) return false;
      if (filtro === "sin-contrasena" && (!u.activo || u.tiene_contrasena !== false)) return false;
      if (!q) return true;
      const etiqueta = etiquetaDePerfil(u.capacidades, perfiles).texto;
      return sinAcentos(
        [u.usuario, u.nombre, nombreDelNodo.get(u.territorio_id ?? -1) ?? "", etiqueta].join(" "),
      ).includes(q);
    });
  }, [busqueda, filtro, filas, nombreDelNodo, perfiles]);

  async function guardar() {
    if (!borrador) return;
    setError(null);
    try {
      if (editando == null) {
        const creado = await crearUsuario({
          usuario: borrador.usuario.trim().toLowerCase(),
          nombre: borrador.nombre.trim(),
          territorio_id: borrador.territorio_id,
          capacidades: borrador.capacidades,
        });
        setFilas((f) => [...f, creado]);
        avisar(`Cuenta ${creado.usuario} creada. Todavía no puede entrar: dale contraseña.`);
      } else {
        const puesto = await editarUsuario(editando, {
          nombre: borrador.nombre.trim(),
          territorio_id: borrador.territorio_id,
          capacidades: borrador.capacidades,
        });
        setFilas((f) => f.map((x) => (x.id === puesto.id ? puesto : x)));
        avisar(`Cuenta ${puesto.usuario} actualizada.`);
      }
      setBorrador(null);
      setEditando(null);
      setAjusteFino(false);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function alternarActivo(u: Usuario) {
    setError(null);
    try {
      const puesto = await editarUsuario(u.id, { activo: !u.activo });
      setFilas((f) => f.map((x) => (x.id === puesto.id ? puesto : x)));
      avisar(`Cuenta ${puesto.usuario} ${puesto.activo ? "activada" : "desactivada"}.`);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function reponerContrasena(u: Usuario) {
    setError(null);
    try {
      const r = await darContrasena(u.id);
      setRecienDada({ usuario: r.usuario, clave: r.contrasena });
      setFilas((f) => f.map((x) => (x.id === u.id ? { ...x, tiene_contrasena: true } : x)));
      avisar(
        r.sesiones_revocadas > 0
          ? `Contraseña nueva para ${r.usuario}. Se cerraron ${r.sesiones_revocadas} sesiones abiertas.`
          : `Contraseña nueva para ${r.usuario}.`,
      );
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (cargando) {
    return (
      <Pantalla titulo="Cuentas">
        <Cargando que="las cuentas" />
      </Pantalla>
    );
  }
  if (error && filas.length === 0) {
    return (
      <Pantalla titulo="Cuentas">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }

  const perfilDelBorrador = borrador ? perfilDe(borrador.capacidades, perfiles) : null;
  const sinContrasena = filas.filter((u) => u.activo && u.tiene_contrasena === false).length;

  const columnas: Columna<Usuario>[] = [
    {
      id: "usuario",
      titulo: "Cuenta",
      clave: (u) => u.usuario,
      celda: (u) => (
        <>
          <strong>{u.nombre}</strong>
          {u.usuario === yo.usuario && (
            <>
              {" "}
              <Insignia tono="primario">tú</Insignia>
            </>
          )}
          <br />
          <span className="usuario apagado chico">{u.usuario}</span>
        </>
      ),
    },
    {
      id: "perfil",
      titulo: "Oficio",
      clave: (u) => etiquetaDePerfil(u.capacidades, perfiles).texto,
      celda: (u) => {
        const e = etiquetaDePerfil(u.capacidades, perfiles);
        if (e.vacia) return <span className="apagado">{e.texto}</span>;
        return e.aMedida ? <Insignia tono="neutro">{e.texto}</Insignia> : e.texto;
      },
    },
    {
      id: "territorio",
      titulo: "Territorio",
      clave: (u) => nombreDelNodo.get(u.territorio_id ?? -1) ?? "",
      celda: (u) =>
        u.capacidades["territorio_completo"] ? (
          <Insignia tono="primario">toda la campaña</Insignia>
        ) : u.territorio_id != null ? (
          (nombreDelNodo.get(u.territorio_id) ?? `nodo ${u.territorio_id}`)
        ) : (
          <span className="apagado">sin territorio</span>
        ),
    },
    {
      id: "estado",
      titulo: "Estado",
      clave: (u) => (u.activo ? (u.tiene_contrasena === false ? 1 : 0) : 2),
      celda: (u) =>
        !u.activo ? (
          <Insignia tono="neutro">inactiva</Insignia>
        ) : u.tiene_contrasena === false ? (
          <Insignia tono="aviso">sin contraseña</Insignia>
        ) : (
          <Insignia tono="exito">activa</Insignia>
        ),
    },
    {
      id: "creado",
      titulo: "Desde",
      clave: (u) => u.creado,
      celda: (u) => <span className="apagado chico">{fecha(u.creado)}</span>,
    },
    {
      id: "acciones",
      titulo: "Acciones",
      sinTitulo: true,
      celda: (u) => (
        <div className="acciones">
          <button
            type="button"
            className="btn terciario fila"
            onClick={() => {
              setEditando(u.id);
              setAjusteFino(false);
              setBorrador({
                usuario: u.usuario,
                nombre: u.nombre,
                territorio_id: u.territorio_id,
                capacidades: { ...u.capacidades },
              });
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn terciario fila"
            onClick={() => void reponerContrasena(u)}
            title="Genera una contraseña nueva, la muestra una vez y cierra las sesiones abiertas de esa persona"
          >
            Dar contraseña
          </button>
          <button
            type="button"
            className={`btn fila ${u.activo ? "peligro" : "terciario"}`}
            onClick={() => void alternarActivo(u)}
          >
            {u.activo ? "Desactivar" : "Activar"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <Pantalla
      titulo="Cuentas"
      descripcion="Quién trabaja en la campaña, con qué oficio y en qué territorio."
      acciones={
        <button
          type="button"
          className="btn primario"
          onClick={() => {
            setBorrador({ ...VACIO, capacidades: {} });
            setEditando(null);
            setAjusteFino(false);
          }}
        >
          Nueva cuenta
        </button>
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {recienDada && (
        <div className="tarjeta aviso" role="alert">
          <h2>Contraseña de {recienDada.usuario}</h2>
          <p className="chico">
            Se muestra una sola vez. Cópiala y entrégasela; si se pierde, se genera otra y la anterior deja
            de servir.
          </p>
          <code className="clave">{recienDada.clave}</code>
          <div className="acciones">
            <button type="button" className="btn secundario" onClick={() => setRecienDada(null)}>
              Ya la copié
            </button>
          </div>
        </div>
      )}

      {borrador && (
        <div className="tarjeta formulario">
          <h2>{editando == null ? "Nueva cuenta" : `Editar ${borrador.usuario}`}</h2>

          <div className="campos-en-fila">
            <div className="campo">
              <label htmlFor="u-nombre">Nombre de la persona</label>
              <input
                id="u-nombre"
                value={borrador.nombre}
                autoComplete="name"
                onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
              />
            </div>
            <div className="campo">
              <label htmlFor="u-usuario">Con qué entra</label>
              <input
                id="u-usuario"
                value={borrador.usuario}
                disabled={editando != null}
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setBorrador({ ...borrador, usuario: e.target.value })}
              />
              {editando == null && <p className="ayuda">Minúsculas, sin espacios. No se puede cambiar después.</p>}
            </div>
          </div>

          <fieldset className="oficios">
            <legend>Oficio</legend>
            <p className="ayuda">
              Elige qué va a hacer esta persona. El oficio marca sus permisos; no hace falta saber cuáles.
            </p>
            <div className="oficios-lista">
              {perfiles.map((p) => (
                <label key={p.clave} className={`oficio${perfilDelBorrador?.clave === p.clave ? " elegido" : ""}`}>
                  <input
                    type="radio"
                    name="oficio"
                    checked={perfilDelBorrador?.clave === p.clave}
                    onChange={() => setBorrador({ ...borrador, capacidades: aplicarPerfil(p) })}
                  />
                  <span>
                    <strong>{p.nombre}</strong>
                    <small>{p.descripcion}</small>
                  </span>
                </label>
              ))}
              {perfilDelBorrador == null && Object.values(borrador.capacidades).some(Boolean) && (
                <p className="ayuda">
                  <Insignia tono="neutro">a la medida</Insignia> Esta cuenta no coincide con ningún oficio: tiene
                  permisos propios. Elegir un oficio los reemplaza.
                </p>
              )}
            </div>
          </fieldset>

          <div className="campo">
            <label htmlFor="u-territorio">Territorio</label>
            <select
              id="u-territorio"
              value={borrador.territorio_id ?? ""}
              onChange={(e) =>
                setBorrador({
                  ...borrador,
                  territorio_id: e.target.value === "" ? null : Number(e.target.value),
                })
              }
            >
              <option value="">Sin territorio</option>
              {asignables.map((n) => (
                <option key={n.id} value={n.id}>
                  {nombreDelNodo.get(n.id)}
                </option>
              ))}
            </select>
            <p className="ayuda">
              Acota lo que esta cuenta ve. Sin territorio no ve nada
              {borrador.capacidades["territorio_completo"]
                ? "; esta cuenta alcanza toda la campaña por su oficio."
                : "."}
            </p>
          </div>

          <details className="ajuste-fino" open={ajusteFino} onToggle={(e) => setAjusteFino((e.target as HTMLDetailsElement).open)}>
            <summary>Ajuste fino: permisos uno por uno</summary>
            <p className="ayuda">
              Para casos que ningún oficio cubre. Al tocar una casilla la cuenta deja de ser ese oficio y pasa a
              ser «a la medida».
            </p>
            <div className="capacidades">
              {capacidades.map((c) => (
                <label key={c.id} className="casilla">
                  <input
                    type="checkbox"
                    checked={Boolean(borrador.capacidades[c.id])}
                    onChange={(e) =>
                      setBorrador({
                        ...borrador,
                        capacidades: { ...borrador.capacidades, [c.id]: e.target.checked },
                      })
                    }
                  />
                  <span>
                    <code>{c.id}</code>
                    <small>{c.descripcion}</small>
                  </span>
                </label>
              ))}
            </div>
          </details>

          {editando == null && (
            <p className="ayuda">
              La cuenta nace sin contraseña y por lo tanto todavía no puede entrar. Dásela con «Dar contraseña»
              en su renglón.
            </p>
          )}

          <div className="acciones">
            <button type="button" className="btn primario" onClick={() => void guardar()}>
              Guardar
            </button>
            <button
              type="button"
              className="btn terciario"
              onClick={() => {
                setBorrador(null);
                setEditando(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="herramientas">
        <label className="solo-lectores" htmlFor="buscar-cuentas">
          Buscar entre las cuentas
        </label>
        <input
          id="buscar-cuentas"
          className="buscar"
          type="search"
          placeholder="Buscar por nombre, usuario, oficio o territorio"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <fieldset className="segmentos" aria-label="Mostrar">
          <legend className="solo-lectores">Mostrar</legend>
          {(
            [
              ["todas", "Todas"],
              ["activas", "Activas"],
              ["inactivas", "Inactivas"],
              ["sin-contrasena", `Sin contraseña${sinContrasena ? ` (${sinContrasena})` : ""}`],
            ] as [Filtro, string][]
          ).map(([f, texto]) => (
            <label key={f} className={filtro === f ? "activo" : undefined}>
              <input type="radio" name="filtro" value={f} checked={filtro === f} onChange={() => setFiltro(f)} />
              {texto}
            </label>
          ))}
        </fieldset>
        <span className="cuenta">
          {visibles.length === filas.length ? `${filas.length} cuentas` : `${visibles.length} de ${filas.length} cuentas`}
        </span>
      </div>

      <Tabla
        columnas={columnas}
        filas={visibles}
        claveDe={(u) => u.id}
        caption="Cuentas de la campaña, con su oficio y su territorio. Las inactivas no pueden entrar; las que no tienen contraseña tampoco, todavía."
        ordenInicial={{ columna: "usuario" }}
        claseDeFila={(u) => (u.activo ? "" : "inactiva")}
        vacio={busqueda ? `Ninguna cuenta coincide con «${busqueda}».` : "Ninguna cuenta en este filtro."}
      />
    </Pantalla>
  );
}
